/** Normalizes GET /influencers/me/profile `active_jobs` items for list + routing. */

import { plainTextFromHtml } from '../../../pages/BrandDashboard/contestWizard/richTextUtils';
import { normalizeUnreadCount } from '../../../utils/campaignThreadUnread';

export interface BuzzBridgeJobListItem {
    /** Stable list key (participant id preferred). */
    id: string;
    campaignId: string;
    /** Campaign-participants document id for creator-workspace routes. */
    participantId: string;
    title: string;
    company: string;
    status: 'active' | 'completed' | 'pending';
    budget: string;
    deadline: string;
    description: string;
    logoUrl?: string;
    /** 0–100 for progress bar UI */
    progressPercent: number;
    milestoneLabel: string;
    rewardDisplay: string;
    /** Card chip: In Progress, Pending Review, Completed */
    statusLabel: string;
    unreadMessageCount: number;
    /** True when campaign_type is commission */
    isCommission?: boolean;
    commissionCode?: string;
    commissionRate?: number | null;
    /** True when campaign has app_install enabled (Circuul campaign) */
    isAppInstall?: boolean;
    /** 'app_install' or 'web_visit' — distinguishes the two Circuul campaign types */
    appInstallKind?: 'app_install' | 'web_visit';
    /** Creator's Circuul referral code for app install campaigns */
    circuulCode?: string;
    /** Cost per install/visit in cents */
    cpaCents?: number;
}

export function extractCampaignId(raw: Record<string, unknown> | null | undefined): string {
    if (!raw || typeof raw !== 'object') return '';
    const c = raw.campaign as unknown;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c && typeof c === 'object' && '_id' in c && (c as { _id?: string })._id) {
        return String((c as { _id: string })._id);
    }
    const cid = raw.campaign_id ?? raw.campaignId;
    if (typeof cid === 'string' && cid) return cid;
    return String(raw._id ?? '');
}

function formatDeadline(raw: Record<string, unknown>): string {
    const candidates = [
        raw.deadline,
        raw.due_at,
        raw.dueAt,
        (raw.campaign as Record<string, unknown> | undefined)?.end_date,
        (raw.campaign as Record<string, unknown> | undefined)?.deadline,
    ];
    for (const v of candidates) {
        if (v == null) continue;
        if (typeof v === 'string' && v) {
            const d = Date.parse(v);
            if (!Number.isNaN(d)) {
                return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
                    new Date(d),
                );
            }
            return v;
        }
    }
    return '—';
}

function formatRewardDisplay(budget: string, budgetVal: unknown): string {
    if (typeof budgetVal === 'number' && !Number.isNaN(budgetVal)) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budgetVal);
    }
    const n = parseFloat(String(budget).replace(/[^0-9.]/g, ''));
    if (!Number.isNaN(n) && String(budget).trim()) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    }
    return budget !== '—' && budget ? budget : '—';
}

function progressForStatus(status: BuzzBridgeJobListItem['status'], raw: Record<string, unknown>): number {
    const p = raw.progress_percentage ?? raw.progressPercent ?? raw.completion_percent;
    if (typeof p === 'number' && !Number.isNaN(p)) {
        return Math.min(100, Math.max(0, p <= 1 ? p * 100 : p));
    }
    if (status === 'completed') return 100;
    if (status === 'pending') return 35;
    return 65;
}

export function mapRawToBuzzBridgeJob(raw: unknown): BuzzBridgeJobListItem | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const campaign = (r.campaign && typeof r.campaign === 'object' ? r.campaign : {}) as Record<string, unknown>;
    const brand = (r.brand && typeof r.brand === 'object' ? r.brand : {}) as Record<string, unknown>;

    const campaignId = extractCampaignId(r);
    const participantId = String(r._id ?? '').trim();
    if (!campaignId) return null;

    const id = participantId || campaignId;

    const title =
        (typeof campaign.title === 'string' && campaign.title) ||
        (typeof r.title === 'string' && r.title) ||
        'Campaign';
    const company =
        (typeof brand.name === 'string' && brand.name) ||
        (typeof campaign.brand_name === 'string' && campaign.brand_name) ||
        (typeof r.company === 'string' && r.company) ||
        '';

    const statusRaw = String(r.status || 'active').toLowerCase();
    let status: BuzzBridgeJobListItem['status'] = 'active';
    if (statusRaw === 'completed' || statusRaw === 'paid' || statusRaw === 'done') status = 'completed';
    else if (statusRaw === 'pending' || statusRaw === 'review' || statusRaw === 'submitted') status = 'pending';

    // For completed campaigns prefer the actual payout amount over the expected budget.
    const payoutCents =
        status === 'completed' && typeof r.payout_amount_cents === 'number' && r.payout_amount_cents > 0
            ? r.payout_amount_cents
            : null;
    const rawBudgetVal =
        payoutCents != null
            ? payoutCents / 100
            : r.possible_earning ?? r.budget ?? campaign.budget ?? campaign.total_budget;
    const totalParticipants =
        typeof campaign.total_participants_count === 'number' && campaign.total_participants_count > 1
            ? campaign.total_participants_count
            : typeof r.total_participants_count === 'number' && (r.total_participants_count as number) > 1
            ? (r.total_participants_count as number)
            : null;
    const budgetVal =
        totalParticipants && typeof rawBudgetVal === 'number' ? rawBudgetVal / totalParticipants : rawBudgetVal;
    const budget =
        typeof budgetVal === 'string' || typeof budgetVal === 'number'
            ? String(budgetVal)
            : typeof campaign.possible_earning === 'string'
            ? campaign.possible_earning
            : '—';

    const rawDesc =
        (typeof campaign.description === 'string' && campaign.description) ||
        (typeof r.description === 'string' && r.description) ||
        '';
    const desc = plainTextFromHtml(rawDesc).slice(0, 120);

    const logoUrl =
        (typeof brand.logo === 'string' && brand.logo) ||
        (typeof campaign.feature_image === 'string' && campaign.feature_image) ||
        (typeof campaign.thumbnail === 'string' && campaign.thumbnail) ||
        undefined;

    const milestoneFromApi =
        (typeof r.next_milestone === 'string' && r.next_milestone) ||
        (typeof r.milestone === 'string' && r.milestone) ||
        '';

    const dl = formatDeadline(r);
    const milestoneLabel =
        milestoneFromApi ||
        (status === 'completed'
            ? 'All deliverables submitted'
            : status === 'pending'
            ? 'Awaiting brand approval'
            : dl !== '—'
            ? `Submit by ${dl}`
            : 'Stay on track with deliverables');

    let statusLabel = 'In Progress';
    if (status === 'pending') statusLabel = 'Pending Review';
    if (status === 'completed') statusLabel = 'Completed';
    if (statusRaw === 'approved') statusLabel = 'Approved';
    if (statusRaw === 'in_progress') statusLabel = 'In Progress';

    const campaignType = String(campaign.campaign_type ?? r.campaign_type ?? '').toLowerCase();
    const isCommission = campaignType === 'commission';
    const commissionCode =
        (typeof r.commission_code === 'string' && r.commission_code) ||
        (typeof (r.commission as { code?: string } | undefined)?.code === 'string' &&
            (r.commission as { code: string }).code) ||
        undefined;
    const commissionRateRaw =
        (r.commission as { rate?: number } | undefined)?.rate ??
        (campaign.commission as { rate?: number } | undefined)?.rate;
    const commissionRate =
        typeof commissionRateRaw === 'number' && !Number.isNaN(commissionRateRaw) ? commissionRateRaw : null;

    // App install (Circuul) campaign detection
    const appInstall = campaign.app_install as Record<string, unknown> | undefined;
    const isAppInstall = Boolean(appInstall?.enabled);
    const appInstallKind: 'app_install' | 'web_visit' | undefined =
        appInstall?.kind === 'web_visit' ? 'web_visit'
        : isAppInstall ? 'app_install'
        : undefined;
    const isWebVisit = appInstallKind === 'web_visit';
    const circuulCode =
        (typeof r.circuul_code === 'string' && r.circuul_code.trim())
            ? String(r.circuul_code).toUpperCase()
            : undefined;
    // Prefer participant-scoped rate (circuul_cpa_cents) over campaign base rate (app_install.cpa_cents)
    const cpaCentsRaw =
        typeof r.circuul_cpa_cents === 'number' ? r.circuul_cpa_cents : appInstall?.cpa_cents;
    const cpaCents = typeof cpaCentsRaw === 'number' && !Number.isNaN(cpaCentsRaw) ? cpaCentsRaw : undefined;

    if (isCommission && !isAppInstall && status !== 'completed') {
        statusLabel = 'Commission';
    }
    if (isAppInstall && status !== 'completed') {
        statusLabel = isWebVisit ? 'Web Visit' : 'App Install';
    }

    return {
        id,
        campaignId,
        participantId,
        title,
        company,
        status,
        budget,
        deadline: dl,
        description: desc,
        logoUrl,
        progressPercent: progressForStatus(status, r),
        milestoneLabel: isAppInstall
            ? circuulCode
                ? `Code ${circuulCode}`
                : isWebVisit ? 'Share your web visit link' : 'Share your referral link'
            : isCommission
            ? commissionCode
                ? `Code ${String(commissionCode).toUpperCase()}`
                : 'Share your commission link'
            : milestoneLabel,
        rewardDisplay: isAppInstall && cpaCents != null
            ? `~${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cpaCents / 100)}/${isWebVisit ? 'visit' : 'install'}`
            : formatRewardDisplay(budget, budgetVal),
        statusLabel,
        unreadMessageCount: normalizeUnreadCount(r.unread_message_count),
        isCommission,
        commissionCode: commissionCode ? String(commissionCode).toUpperCase() : undefined,
        commissionRate,
        isAppInstall,
        appInstallKind,
        circuulCode,
        cpaCents,
    };
}

export function mapActiveJobsToListItems(jobs: unknown[] | undefined): BuzzBridgeJobListItem[] {
    if (!jobs?.length) return [];
    return jobs.map(mapRawToBuzzBridgeJob).filter((j): j is BuzzBridgeJobListItem => j != null);
}
